import theme from '../../theme';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';

const EXPLAINERS_CSS = `
  .explainers-wrapper {
    max-width: 900px;
    margin: 0 auto;
    padding: 0 20px 48px;
  }
  .explainers-grid {
    display: flex;
    flex-direction: column;
    gap: 32px;
    margin-top: 24px;
  }
  .explainer-card {
    background: ${theme.bgCard};
    border: 1px solid rgba(184, 149, 90, 0.25);
    border-radius: 4px;
    padding: 36px 40px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    position: relative;
    overflow: hidden;
  }
  .explainer-card::after {
    content: '';
    position: absolute;
    bottom: 0;
    right: 0;
    width: 16px;
    height: 16px;
    background: linear-gradient(135deg, transparent 50%, rgba(184, 149, 90, 0.15) 50%, rgba(184, 149, 90, 0.3) 100%);
    border-top: 1px solid rgba(184, 149, 90, 0.15);
    border-left: 1px solid rgba(184, 149, 90, 0.15);
    pointer-events: none;
  }
  .explainer-card:hover {
    transform: translateY(-3px);
    border-color: rgba(184, 149, 90, 0.55);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.4);
  }
  .explainer-meta {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 12px;
    font-weight: 400;
    letter-spacing: 0.18em;
    color: ${theme.gold};
    text-transform: uppercase;
    margin-bottom: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .explainer-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: 26px;
    font-weight: 400;
    color: ${theme.textPrimary};
    margin: 0 0 16px 0;
    line-height: 1.25;
    letter-spacing: 0.01em;
  }
  .explainer-body {
    font-family: 'EB Garamond', serif;
    font-size: 16.5px;
    color: ${theme.textMuted};
    line-height: 1.7;
  }
  .explainer-body p + p {
    margin-top: 12px;
  }
  .explainer-tool-link {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 24px;
    font-family: 'Josefin Sans', sans-serif;
    font-size: 12px;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: ${theme.gold};
    text-decoration: underline;
    text-underline-offset: 4px;
    transition: opacity 0.2s ease;
  }
  .explainer-tool-link:hover {
    opacity: 0.8;
  }
  @media (max-width: 600px) {
    .explainer-card {
      padding: 24px;
    }
    .explainer-title {
      font-size: 22px;
    }
  }
`;

const explainersData = [
  {
    category: 'Method',
    num: '01',
    title: 'How Austrians Know What They Claim to Know',
    body: (
      <>
        <p>
          Mises's Human Action begins from a single premise: humans act. Not react, like chemicals in a reaction - act, purposefully, to replace a less satisfactory state of affairs with a more satisfactory one. Praxeology is the deductive science of what follows logically from that premise: that action implies choice, choice implies scarcity, scarcity implies cost, and so on, the way theorems follow from axioms in geometry.
        </p>
        <p>
          This puts Austrian method at odds with how most modern economics is done. Mainstream economics treats itself as an empirical science: build a model, gather data, test the model, revise. Praxeology treats economic law as something derived by logic first and illustrated by history, not proven by it - because human choices aren't repeatable experiments. This is also the most contested part of the Austrian program: critics call it unfalsifiable; Austrians call the alternative a category error, applying the method of physics to a subject matter it was never built to describe.
        </p>
      </>
    ),
    toolText: 'Ask Austrian AI Assistant',
    toolPath: '/ask-austrian'
  },
  {
    category: 'Value Theory',
    num: '02',
    title: "Why Nothing Has a 'Right' Price",
    body: (
      <>
        <p>
          Classical economists like Smith and Ricardo were stuck on a puzzle: water is essential to life and nearly free; diamonds are useless by comparison and extremely dear. If value came from a good's usefulness, or the labor required to produce it, this made no sense.
        </p>
        <p>
          The Marginal Revolution of 1871 - Menger working independently of Jevons and Walras - resolved it. Value isn't a property of "water" or "diamonds" in the abstract. Acting individuals value the next unit of a good, given how much they already have. Your millionth liter of water is worth almost nothing to you; the one diamond you don't own is worth a great deal. Value is assigned at the margin, not built into the object - which is also why voluntary exchange isn't zero-sum. Both sides value what they're receiving more than what they're giving up, or the trade wouldn't happen.
        </p>
      </>
    ),
    toolText: 'Open Calculation Simulator',
    toolPath: '/calculation-problem'
  },
  {
    category: 'Market Process',
    num: '03',
    title: 'Who Actually Does the Discovering',
    body: (
      <>
        <p>
          Mainstream models often start from equilibrium - prices already reflect all available information, markets already clear. Austrian economics asks a different question: who does the work of getting there? Israel Kirzner's answer is the entrepreneur - not primarily an innovator or a risk-taker in the textbook sense, but someone alert to opportunities that already exist and simply haven't been noticed yet: a price discrepancy between two markets, an unmet want, a resource being used somewhere less valuable than it could be.
        </p>
        <p>
          Acting on that alertness - buying where something is underpriced, redirecting resources toward what's more urgently wanted - is what actually moves prices toward reflecting real scarcity and demand. It's the mechanism behind Hayek's claim that prices carry knowledge: prices don't adjust themselves. Profit is the reward for correctly perceiving what everyone else missed; loss is the penalty for perceiving it wrongly. Take the entrepreneur out of the picture, and the price system has no engine - only a destination nobody is driving toward.
        </p>
      </>
    ),
    toolText: 'Ask Austrian AI Assistant',
    toolPath: '/ask-austrian'
  },
  {
    category: 'Knowledge',
    num: '04',
    title: 'Why Prices Carry Knowledge',
    body: (
      <>
        <p>
          Building on Friedrich Hayek's seminal essay "The Use of Knowledge in Society", prices are not merely ratios at which goods are exchanged; they are an information communication system. The critical knowledge required to coordinate an economy is dispersed, subjective, and tacit. It cannot be aggregated or calculated by a central planning board.
        </p>
        <p>
          When scarcity changes or consumer preferences shift, prices adjust. This signal coordinates millions of independent decisions without anyone needing to know the specific cause of the change. When interest rates or currency values are manipulated by central policy, the information carrying capacity of prices is corrupted, leading to widespread structural mistakes.
        </p>
      </>
    ),
    toolText: 'View Interest Rate Data',
    toolPath: '/fred'
  },
  {
    category: 'Money',
    num: '05',
    title: 'Where Money Actually Comes From',
    body: (
      <>
        <p>
          Money is not a government invention. Carl Menger's regression theorem explains its origin as spontaneous, not designed: in a barter economy, traders eventually notice that some goods - cattle, salt, eventually precious metals - are more marketable than others: easier to divide, store, and get others to accept in trade. Traders start accepting that good not to consume it, but because they expect others will accept it from them later. Once enough people do this, it stops being a commodity that's also sometimes used as money, and becomes money.
        </p>
        <p>
          This matters for everything else here. A central bank issuing new currency, or a bank lending out more in claims than it holds in reserve, isn't creating one more good among many - it's altering the supply of the good every other price is quoted in. That's the institutional starting point for the credit expansion mechanics in the Garrison Triangle: an interest rate distorted by new money looks, to a borrower, exactly like a genuine increase in savings. The calculation problem and the business cycle are, underneath, both stories about what happens when that signal is corrupted.
        </p>
      </>
    ),
    toolText: 'Launch Garrison Triangle Model',
    toolPath: '/garrison-triangle'
  },
  {
    category: 'Calculation',
    num: '06',
    title: 'The Calculation Problem in Practice',
    body: (
      <>
        <p>
          The Socialist Calculation Debate, initiated by Ludwig von Mises, demonstrated that rational economic allocation is impossible without market prices. Without private ownership of capital goods, there is no market for them, and without a market, there are no prices. Without prices, cost-risk calculations cannot be computed.
        </p>
        <p>
          The calculation problem is not just historical. Any attempt to dictate interest rates, credit flows, or resource pricing today introduces the same failures on a smaller scale. Evaluating any plan - a business, a public program, a five-year target - means asking how well it lets real prices emerge, rather than relying on subsidies or distorted credit to paper over the guesswork.
        </p>
        <p>
          <strong>A common objection:</strong> starting in the 1930s, economists Oskar Lange and Abba Lerner argued Mises was wrong - a planning board could mimic market prices through trial-and-error adjustment, or, later critics argued, a computer could simply solve the resource-allocation equations directly.
        </p>
        <p>
          Hayek's response reframed the problem. It was never fundamentally about computation. It's a knowledge problem. The information prices carry doesn't exist anywhere as centralized, quantifiable data a planning board or a computer could solve for - it exists as fragmented, often tacit knowledge held by millions of individuals, continuously revealed only through the act of real people bidding, buying, and abstaining with real property at stake. A model can be fed data. It cannot be fed knowledge that hasn't been produced yet by the very market process it's trying to replace.
        </p>
      </>
    ),
    toolText: 'Open Calculation Simulator',
    toolPath: '/calculation-problem'
  },
  {
    category: 'Capital',
    num: '07',
    title: 'Time Preference and Capital Structure',
    body: (
      <>
        <p>
          Mainstream economic theory often treats capital as a homogeneous fund, denoted simply as "K". Austrian economics rejects this. Capital is a highly complex, heterogeneous structure composed of specific machines, buildings, and tools that must be aligned sequentially in time.
        </p>
        <p>
          This structure is bounded by time preference, the degree to which actors value current goods over future goods. A lower time preference leads to increased savings, which lowers the interest rate and extends the structure of production to support more complex, productive processes. Without real savings to support these structures, long-duration projects cannot be completed, leading to structural liquidations.
        </p>
      </>
    ),
    toolText: 'Ask Austrian AI Assistant',
    toolPath: '/ask-austrian'
  },
  {
    category: 'Coordination',
    num: '08',
    title: 'What the Garrison Triangle Shows',
    body: (
      <>
        <p>
          The Garrison model coordinates macroeconomics with capital theory, illustrating how consumption and investment decisions are balanced over time. In a healthy economy, the rate of interest acts as a price signal that coordinates the plans of savers with those of capital builders.
        </p>
        <p>
          If consumers choose to save more, the interest rate falls, signaling to businesses that they can sustainably finance longer, more capital-intensive stages of production (early stages). However, if the central bank artificially lowers interest rates through credit expansion, it creates a clashing signal: consumers consume more because credit is cheap, while businesses overinvest in early-stage capital projects. This coordination failure causes systemic malinvestment, which eventually leads to a recession.
        </p>
        <p>
          <strong>A common objection:</strong> critics - including some mainstream business-cycle theorists - ask why rational entrepreneurs would be systematically fooled by artificially low interest rates, cycle after cycle. If markets are efficient and actors learn from experience, shouldn't they eventually see through it?
        </p>
        <p>
          The Austrian response is that this isn't a story about entrepreneurs being irrational or forgetful. A low interest rate caused by genuine increased saving and one caused by credit expansion look identical at the moment a business owner decides whether to start a ten-year project. Both say: capital is more available, borrow now. The rational response to that signal is the same either way - the difference only becomes visible later, when the real savings needed to complete every project turns out not to exist. The deception isn't in the entrepreneur's judgment. It's baked into the price itself.
        </p>
      </>
    ),
    toolText: 'Launch Garrison Triangle Model',
    toolPath: '/garrison-triangle'
  },
  {
    category: 'Stewardship',
    num: '09',
    title: 'Why Austrian Economics Still Matters for Long-Term Thinking',
    body: (
      <>
        <p>
          Mainstream finance models assume markets are in continuous equilibrium and attempt to manage risk through historical volatility metrics. Austrian economics, by contrast, focuses on uncertainty, subjective expectation, and dynamic coordination processes.
        </p>
        <p>
          For anyone thinking in decades rather than quarters, the Austrian framework offers a defense against credit-induced bubbles: it explains why long-duration projects carry distinct risks during credit-driven malinvestment phases, and why the resilience of a structure - not just its size - is what matters.
        </p>
      </>
    ),
    toolText: 'Take Conceptual Quiz',
    toolPath: '/quiz'
  }
];

function ExplainersPage() {
  useEffect(() => {
    document.title = "Explainers - Austrian Process";
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    const previousCanonical = canonicalLink ? canonicalLink.getAttribute('href') : '';
    if (canonicalLink) {
      canonicalLink.setAttribute('href', 'https://www.austrianprocess.com/explainers');
    }
    return () => {
      if (canonicalLink && previousCanonical) {
        canonicalLink.setAttribute('href', previousCanonical);
      }
    };
  }, []);

  return (
    <div className="explainers-wrapper">
      <style>{EXPLAINERS_CSS}</style>
      <PageHeader 
        title="Explainers" 
        description="Conceptual briefs on the core ideas of Austrian economics, from first principles to their real-world implications. Last reviewed: September 2026."
      />
      <div className="explainers-grid">
        {explainersData.map((exp, idx) => (
          <div key={idx} className="explainer-card">
            <div className="explainer-meta">
              <span>{exp.category}</span>
              <span>{exp.num}</span>
            </div>
            <h2 className="explainer-title">{exp.title}</h2>
            <div className="explainer-body">{exp.body}</div>
            <Link to={exp.toolPath} className="explainer-tool-link">
              {exp.toolText} &rarr;
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ExplainersPage;
